import { Component, computed, effect, ElementRef, EventEmitter, HostBinding, inject, Input, OnDestroy, Output, signal, untracked, viewChild } from '@angular/core';

import { ButtonMiniFabComponent } from 'src/app/features/shared/controls/container/button-mini-fab/button-mini-fab.component';
import { FilterComponent } from 'src/app/features/shared/filter/container/filter.component';
import { pageMode, TPageMode } from 'src/app/infrastructure/models/page-mode';
import { PagerService } from 'src/app/features/shared/pager/pager.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { _TabPageService } from 'src/app/features/shared/central-page/services/_tab-page.service';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { centralPageAnimation } from '../../../../../../../infrastructure/animations/central-page-animations';
import { showAnimation } from 'src/app/infrastructure/animations/show-animation';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

@Component({
    selector: 'app-tablist-footer',
    templateUrl: './tablist-footer.component.html',
    styleUrls: ['./tablist-footer.component.scss'],
    standalone: true,
    imports: [
        ButtonMiniFabComponent,
        FilterComponent,
        SharedModule,
    ],
    animations: [
        centralPageAnimation,
        showAnimation,
    ],
})
export class TablistFooterComponent
    implements OnDestroy
{
    private readonly _pagerService = injectRequired(PagerService);
    private readonly _tabPageService = injectRequired(_TabPageService);
    private readonly __tabListService = inject(TabListService, {optional: true});

    logging = true;
    pagerEnabled: boolean = false;

    sectionDetails_show$$ = this._tabPageService.sectionDetails_show$$;
    tabVisible$$ = this._tabPageService.tabVisible$$;
    // btnEditData$$ is al claim-gated (claimName.btnPageEdit_Show) en hide-genormaliseerd in _CentralPageService.
    btnEditShow$$ = computed(() => !this._tabPageService.btnEditData$$().hide);
    // Disabled = recht aanwezig maar op deze omgeving vergrendeld (zie _CentralPageService.__sectionButton_setAuth).
    btnEditDisabled$$ = computed(() => this._tabPageService.btnEditData$$().disabled);
    btnEditTooltip$$ = computed(() => this._tabPageService.btnEditData$$().tooltip || 'edit page');
    tabPositionOffStandard$$ = computed(() => this.__tabListService?.tabPositionOffStandard$$() ?? false);
    locked$$ = computed(() => this.__tabListService?.locked$$() ?? false);
    // Zonder tabs is de hele footer (tab-/filter-knoppen) zinloos → verbergen. In de
    // single-page-context (geen TabListService) is er geen tab-lijst en blijft hij tonen.
    show$$ = computed(() => this.__tabListService?.hasList$$() ?? true);
    filterShow$$ = this._pagerService.filterShow$$;
    pagerMode$$ = this._pagerService.pagerMode$$;

    // De vlottende tab-knoppen (overlay) volgen de werkelijke hoogte van de filterbalk i.p.v. een
    // hardgecodeerde per-stand offset; zo schuiven ze ook mee als het filter-instellingenpaneel
    // openklapt of de veld-chips naar een extra regel wrappen. Zie [@style.bottom.px] in de template.
    private readonly __pagerContainer$$ = viewChild<ElementRef<HTMLElement>>('pagerContainer');
    protected readonly _pagerHeight$$ = signal(0);
    private __resizeObserver: ResizeObserver | null = null;

    @Input() buttonsShow = true;

    // Elke lijst-tool registreert zijn eigen footer-template voor de tab (tabList_addFooter). Wisselt de
    // tab van lijst-component (app-mode-swap: view ↔ edit), dan wordt deze footer opnieuw aangemaakt en
    // speelden de in-animaties (@show, @showHeightCollapse) opnieuw → flikkerende knoppen. Vervangt deze
    // footer binnen __replaceWindowMs een footer van dezelfde tab, dan zonder animatie; een gewone
    // tabwissel (andere tab) of de eerste keer laden animeert zoals altijd.
    private static readonly __replaceWindowMs = 300;
    private static __lastDestroyed: { tabIndex: number | null; time: number } | null = null;
    private readonly __tabIndex = untracked(() => this.__tabListService?.tabIndex$$() ?? null);
    @HostBinding('@.disabled') protected readonly _animationsDisabled = (() => {
        const __last = TablistFooterComponent.__lastDestroyed;
        return !!__last && __last.tabIndex === this.__tabIndex
            && performance.now() - __last.time < TablistFooterComponent.__replaceWindowMs;
    })();

    @Output('onFilterChange') filterChangeEmit = new EventEmitter();

    constructor() {
        effect(() => {
            const _filterShow = this._pagerService.filterShow$$() ?? false;
            const _pagerShow = this._pagerService.pagerShow$$();

            this._pagerService.pagerMode_set(_pagerShow, _filterShow);
        })

        // (Her)koppel de ResizeObserver aan de filterbalk zodra die in/uit de DOM komt (@if).
        // Is de balk afwezig (tab/filter dicht), dan val terug op hoogte 0 → knoppen tegen de onderrand.
        effect((onCleanup) => {
            const _element = this.__pagerContainer$$()?.nativeElement ?? null;

            this.__resizeObserver?.disconnect();

            if (!_element) {
                this._pagerHeight$$.set(0);
                return;
            }

            this.__resizeObserver = new ResizeObserver(entries => {
                this._pagerHeight$$.set(entries[0]?.contentRect.height ?? 0);
            });
            this.__resizeObserver.observe(_element);

            onCleanup(() => this.__resizeObserver?.disconnect());
        });
    }

    emit_editShow() {
        this._tabPageService.pageMode_set(pageMode.editPage);
    }

    emit_btnFilterShow() {
        this._pagerService.filterShow_toggle();
    }

    emit_filterChange(): void {
        this.filterChangeEmit.emit();
    }

    emit_positionReset() {
        this.__tabListService?.position_reset();
    }

    emit_tabHide() {
        if (this.__tabListService) {
            this.__tabListService.position_hide();
        } else {
            this._tabPageService.pageMode_set(pageMode.detailsPage);
        }
    }

    emit_tabShow() {
        if (this.__tabListService) {
            this.__tabListService.position_restore();
        } else {
            this._tabPageService.pageMode_set(pageMode.tabPage);
        }
    }

    ngOnDestroy(): void {
        TablistFooterComponent.__lastDestroyed = { tabIndex: this.__tabIndex, time: performance.now() };
        this.__resizeObserver?.disconnect();
        this.filterChangeEmit?.unsubscribe();
    }
}
